import { useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import { useAuth } from "@/auth/use-auth";
import { usePermissions } from "@/lib/auth/usePermissions";
import { playDing, saveLiveAlertPrefs, useLiveAlertPrefs } from "@/realtime/alertPrefs";
import type { ChangeMessage } from "@tcg/realtime";
import { useLiveTopics } from "@/realtime/hooks";

export function isNewOrder(message: ChangeMessage): boolean {
  return message.entity === "order" && message.action === "created";
}

export interface NewOrders {
  count: number;
  reset(): void;
  sound: boolean;
  setSound(on: boolean): void;
  /** `live` = realtime connected; otherwise the 60-second refresh finds new orders. */
  live: boolean;
}

/**
 * "New order" badge of the home page (T11-10): the realtime hub's `staff:orders` change with
 * `order created` adds one at once and refreshes the to-do counters. The toast + quiet sound of a
 * new order come from the app-wide live alerts (T11-52) — the sound switch here is the same
 * per-person setting. Without realtime the orders-today counter of the 60 s refresh is compared
 * instead (and the sound plays here, since no live alert fires then — same switches).
 */
export function useNewOrders(
  ordersToday: number | null,
  options: { play?: () => void } = {},
): NewOrders {
  const perms = usePermissions();
  const { session } = useAuth();
  const staffId = session?.user.id ?? "anonymous";
  const queryClient = useQueryClient();
  const play = options.play ?? playDing;
  const prefs = useLiveAlertPrefs(staffId);
  const [count, setCount] = useState(0);
  const prefsRef = useRef(prefs);
  useEffect(() => {
    prefsRef.current = prefs;
  });

  const arrived = useCallback(
    (n: number, viaRealtime: boolean) => {
      setCount((c) => c + n);
      const { sound, toasts } = prefsRef.current;
      // realtime: the app-wide live alert rings (T11-52); here only for the fallback refresh
      if (!viaRealtime && sound && toasts) {
        play();
      }
      void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
    },
    [play, queryClient],
  );

  const status = useLiveTopics(
    ["staff:orders"],
    (message) => {
      if (isNewOrder(message)) {
        arrived(1, true);
      } else if (message.entity === "order" || message.entity === "payment") {
        void queryClient.invalidateQueries({ queryKey: ["dashboard"] });
      }
    },
    {
      enabled: perms.can("orders.read"),
      onResync: () => void queryClient.invalidateQueries({ queryKey: ["dashboard"] }),
    },
  );
  const live = status === "live";

  // fallback: orders placed today went up between two refreshes
  const previous = useRef<number | null>(null);
  useEffect(() => {
    if (ordersToday === null) {
      return;
    }
    const before = previous.current;
    previous.current = ordersToday;
    if (!live && before !== null && ordersToday > before) {
      arrived(ordersToday - before, false);
    }
  }, [ordersToday, live, arrived]);

  const setSound = useCallback(
    (on: boolean) => {
      saveLiveAlertPrefs(staffId, { ...prefsRef.current, sound: on });
    },
    [staffId],
  );

  return {
    count,
    reset: () => {
      setCount(0);
    },
    sound: prefs.sound,
    setSound,
    live,
  };
}
